import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Box, { BoxProps } from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { Badge, Typography, useTheme } from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Home as HomeLucide, UserPlus2, MessageSquare } from 'lucide-react';

import { INVITE_PLAYER_ROUTE } from '../invite-players';
import { InviteNotifier } from './invite-notifier';
import { ProfileButton } from './profile-button';
import { useSelector } from 'react-redux';
import { Store } from '../../../redux';
import { IN_PLAY_ROUTE } from '../in-play';
import { DownIcon, UpIcon } from '../../../assets';
import { LeaveRoom } from './leave-room';
import {
  BhabiLink,
  BhabiWordmark,
  TopBarLayout,
  UserAvatar,
} from '../../../shared/components';
import { CoinPile } from '@/shared/components';
import { DM_PAGE_ROUTE } from '../chat';
import { useDmPageQuery, useGetFirebaseUsers } from '../chat/dm';
import { PUBLIC_PROFILE_ROUTE } from '../profile';
import { HOME_ROUTE } from '../home';
import { isFunction } from 'lodash';


import { SendMessage } from '../use-websocket';
import { COINS_INFO_ROUTE } from '../coins-info';
interface TopBarProps {
  sendMessage?: SendMessage;
  children?: (props: { isTopBarVisible: boolean }) => JSX.Element;
}

export const PrimaryTopBar: FC<TopBarProps> = ({ sendMessage, children }) => {
  const nav = useNavigate();
  const { isInRoom, coins } = useSelector((state: Store) => ({
    isInRoom: state.userReducer.isInRoom,
    coins: state.userReducer.coins,
  }));
  const room = useSelector(
    (state: Store) => state.inPlayReducer.gameInfo?.room
  );

  const [slideTopBar, setSlideTopBar] = useState(false);
  const { pathname } = useLocation();

  return (
    <>
      <TopBarLayout
        outerBoxProps={{
          hidden: slideTopBar,
        }}
      >
        <TopBarLogoOrBackButton isInRoom={!!isInRoom} />
        {/* Coins — secondary, but always one tap away */}
        <button
          type="button"
          className="bh-coin-chip"
          aria-label={`${coins || 0} coins. Open coins info`}
          onClick={() => {
            nav(COINS_INFO_ROUTE.ABSOLUTE_PATH);
          }}
        >
          <CoinPile size="sm" />
          <span aria-hidden="true">{coins || 0}</span>
        </button>

        {/* Hidden on mobile — Messages is in the profile dropdown there. */}
        <Box
          sx={theme => ({
            display: 'flex',
            alignItems: 'center',
            [theme.breakpoints.down('sm')]: { display: 'none' },
          })}
        >
          <ChatNotification />
        </Box>
        {!room?.isFull && (
          <Box display="flex" alignItems="center">
            <IconButton
              aria-label="Invite players"
              sx={{ width: 44, height: 44 }}
              onClick={() => {
                nav(INVITE_PLAYER_ROUTE.ABSOLUTE_PATH);
              }}
            >
              <UserPlus2 size={22} />
            </IconButton>
            <InviteNotifier />
          </Box>
        )}

        <ProfileButton hideLogout={isInRoom} />
        {isInRoom && <LeaveRoom sendMessage={sendMessage} />}
      </TopBarLayout>
      {/*Disabling this feature for now. */}
      {isInRoom && pathname === IN_PLAY_ROUTE.ABSOLUTE_PATH && false && (
        <CollapseTopBar
          setSlideTopBar={setSlideTopBar}
          slideTopBar={slideTopBar}
        />
      )}

      {isFunction(children) && children({ isTopBarVisible: !slideTopBar })}
    </>
  );
};

export const CollapseTopBar: FC<{
  setSlideTopBar: Dispatch<SetStateAction<boolean>>;
  slideTopBar: boolean;
}> = ({ setSlideTopBar, slideTopBar }) => {
  const Icon = slideTopBar ? DownIcon : UpIcon;
  return (
    <Box
      sx={{
        position: 'fixed',
        top: !slideTopBar ? 42 : 1,
        left: '47%',
        zIndex: 3000,
        pt: 'env(safe-area-inset-top)',
      }}
    >
      <Icon
        sx={{
          cursor: 'pointer',
          fontSize: 40,
        }}
        onClick={() => {
          setSlideTopBar(prev => !prev);
        }}
      />
    </Box>
  );
};

export const TopBarLogoOrBackButton: FC<{
  outerBoxProps?: BoxProps;
  isInRoom: boolean;
}> = ({ outerBoxProps, isInRoom }) => {
  const { pathname } = useLocation();

  const nav = useNavigate();

  const homePage = isInRoom
    ? IN_PLAY_ROUTE.ABSOLUTE_PATH
    : HOME_ROUTE.ABSOLUTE_PATH;

  return (
    <Box {...outerBoxProps}>
      {pathname === HOME_ROUTE.ABSOLUTE_PATH ||
      pathname === IN_PLAY_ROUTE.ABSOLUTE_PATH ? (
        <BhabiWordmark
          to={homePage}
          label={isInRoom ? 'Back to your table' : 'Bhabi home'}
        />
      ) : (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box>
            <IconButton
              aria-label="Go back"
              sx={{ width: 44, height: 44 }}
              onClick={() => {
                nav(-1);
              }}
            >
              <ArrowBackOutlinedIcon fontSize="small" />
            </IconButton>
          </Box>
          {/* Explicit Home shortcut — keeps the chat / detail pages from
              feeling like dead-ends. Hides itself only when we're already
              on the home page (handled by the outer branch). */}
          <IconButton
            component={BhabiLink}
            to={homePage}
            aria-label={isInRoom ? 'Back to your table' : 'Go to home'}
            sx={{ width: 44, height: 44 }}
          >
            <HomeLucide size={18} />
          </IconButton>

          <HomeButton homePage={homePage} />
        </Box>
      )}
    </Box>
  );
};

const HomeButton: FC<{ homePage: string }> = ({ homePage }) => {
  const [params] = useSearchParams();
  const receiverID = params.get('receiverID');
  const { mutate: getUserInfo, data } = useGetFirebaseUsers();
  const theme = useTheme();

  useEffect(() => {
    if (!receiverID) {
      return;
    }

    getUserInfo([receiverID]);
  }, [getUserInfo, receiverID]);

  return (
    <>
      {receiverID && data?.[0] ? (
        <Box
          sx={theme => ({
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            // On mobile the chat panel renders its own header with the
            // receiver's avatar + name + back button, so the global crumb
            // is redundant noise.
            [theme.breakpoints.down('sm')]: { display: 'none' },
          })}
        >
          <UserAvatar
            userState={data[0].state}
            photoURL={data[0].photoURL}
            name={data[0].name}
            avatarProps={{
              sx: {
                width: 25,
                height: 25,
              },
            }}
          />
          <BhabiLink
            to={[PUBLIC_PROFILE_ROUTE.EXACT_PATH, '?user_id=', data[0].id].join(
              ''
            )}
            style={{
              color: theme.palette.text.primary,
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
            }}
          >
            {/* Receiver name is hidden on phones to keep the top bar uncluttered. */}
            <Typography
              variant="body2"
              sx={theme => ({
                maxWidth: 160,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                [theme.breakpoints.down('sm')]: { display: 'none' },
              })}
            >
              {data[0].name}
            </Typography>
            <NavigateNextIcon />
          </BhabiLink>
        </Box>
      ) : null}
    </>
  );
};

export const ChatNotification: FC = () => {
  const nav = useNavigate();
  const { firebaseUID } = useSelector((state: Store) => state.userReducer);

  const {
    pageData: { uniqueMessagePerUser },
  } = useDmPageQuery(false);

  const messagesNotRead = useMemo(
    () =>
      uniqueMessagePerUser.filter(
        message =>
          message.senderID !== firebaseUID && message.isRead === 'false'
      ).length,
    [uniqueMessagePerUser, firebaseUID]
  );

  return (
    <IconButton
      aria-label={
        messagesNotRead
          ? `Messages, ${messagesNotRead} unread`
          : 'Messages'
      }
      sx={{ width: 44, height: 44 }}
      onClick={() => {
        nav(DM_PAGE_ROUTE.ABSOLUTE_PATH);
      }}
    >
      <Badge badgeContent={messagesNotRead} color="success">
        <MessageSquare size={22} />
      </Badge>
    </IconButton>
  );
};
